import React, { useMemo, useState } from "react";
import { MapPin, Lock, ChevronDown, X } from "lucide-react";

/**
 * Compact destination selector for the registered client.
 * Hidden by the parent if user is admin-locked to a single destination
 * or has fewer than 2 destinations available.
 */
export const DestinationSelector = ({
  destinations = [],
  selectedDest = null,
  onChange = () => {},
  isLocked = false,
  className = "",
  forceOpen = false,
  onForceClose = () => {},
  mandatory = false,
}) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");

  const current = destinations.find((d) => d.id === selectedDest) || null;
  const isOpen = (open || forceOpen) && !isLocked;
  // In modalità obbligatoria senza scelta, il popup non può essere chiuso
  const canDismiss = !mandatory || !!current;
  const close = () => {
    setOpen(false);
    onForceClose();
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return destinations;
    return destinations.filter((d) => {
      const txt = [d.codice_dest, d.denominazione, d.indirizzo, d.citta].filter(Boolean).join(" ").toLowerCase();
      return txt.includes(q);
    });
  }, [destinations, filter]);

  const label = current
    ? current.indirizzo || current.denominazione || `Dest. ${current.codice_dest}`
    : "Seleziona destinazione…";
  const subline = current
    ? [current.citta, current.denominazione].filter(Boolean).join(" · ")
    : `${destinations.length} destinazioni disponibili`;

  return (
    <div className={`relative ${className}`} data-testid="dest-selector">
      <button
        type="button"
        onClick={() => !isLocked && setOpen(true)}
        disabled={isLocked && !current}
        data-testid="dest-selector-btn"
        className={`w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition text-left ${
          isLocked
            ? "border-amber-500/30 bg-amber-500/5 cursor-not-allowed"
            : "border-cyan-500/30 bg-cyan-500/5 hover:bg-cyan-500/10"
        }`}
      >
        {isLocked ? (
          <Lock className="w-4 h-4 text-amber-400 shrink-0" />
        ) : (
          <MapPin className="w-4 h-4 text-cyan-400 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">
            {isLocked ? "Destinazione (assegnata)" : "Destinazione di consegna"}
          </p>
          <p className="text-sm font-semibold text-white leading-tight truncate">{label}</p>
          {subline && (
            <p className="text-[10px] text-zinc-500 truncate">{subline}</p>
          )}
        </div>
        {!isLocked && <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" />}
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={() => canDismiss && close()}
          data-testid="dest-selector-overlay"
        >
          <div
            className="bg-[#18181b] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:w-[420px] sm:max-h-[80vh] max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-3 border-b border-white/5">
              <h3 className="text-sm font-bold text-white">
                {mandatory && !current ? "Per quale punto vendita stai ordinando?" : "Scegli destinazione"}
              </h3>
              {canDismiss && (
                <button
                  type="button"
                  onClick={close}
                  data-testid="dest-selector-close"
                  className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-white/5"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {mandatory && !current && (
              <p className="px-3 pt-2 text-[11px] text-amber-300" data-testid="dest-mandatory-hint">
                Scegli il punto vendita: lo storico articoli, gli ultimi prezzi e l&apos;ordine
                faranno riferimento a questo punto vendita.
              </p>
            )}
            <div className="p-3 border-b border-white/5">
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Cerca per nome, indirizzo o città..."
                data-testid="dest-selector-search"
                className="w-full px-3 py-2 rounded-md bg-[#0f0f11] border border-white/10 text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-cyan-500/40"
              />
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-white/5">
              {filtered.length === 0 && (
                <p className="p-6 text-center text-sm text-zinc-500">Nessuna destinazione</p>
              )}
              {filtered.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => {
                    onChange(d.id);
                    close();
                  }}
                  data-testid={`dest-option-${d.id}`}
                  className={`w-full text-left p-3 hover:bg-white/5 transition ${
                    d.id === selectedDest ? "bg-cyan-500/10" : ""
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-white truncate">
                        {d.indirizzo || d.denominazione || `Dest. ${d.codice_dest}`}
                      </p>
                      <p className="text-[12px] font-semibold text-cyan-300 truncate">
                        {[d.citta, d.provincia ? `(${d.provincia})` : null, d.cap]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      <p className="text-[10px] text-zinc-500 truncate mt-0.5">
                        {d.denominazione ? `${d.denominazione} · ` : ""}
                        <span className="font-mono text-zinc-600">Cod. {d.codice_dest}</span>
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
